import { useCallback, useEffect, useRef, useState } from 'react';

/* ═══════════════════════════════════════════════════════════════════════
   useRequete — l'état d'une donnée chargée depuis le serveur

   statut      'chargement' (rien encore) | 'pret' | 'erreur'
   actualisation  true quand des données SONT DÉJÀ À L'ÉCRAN et qu'on les
                  recharge : on ne les efface pas pour un squelette
   perimee     true si elles ont plus de `perimeApres` ms (défaut 5 min)
   majLe       l'heure de la dernière réponse
   recharger() relance ; setDonnees() pour une mise à jour optimiste

   Revenir sur l'onglet après `perimeApres` relance d'elle-même, en
   gardant l'écran tel quel pendant ce temps.

   `charger` renvoie les données, ou lève une erreur — de préférence avec
   `erreur.statut` (le code HTTP), que `EtatDonnees` traduit en écran.
   ═══════════════════════════════════════════════════════════════════════ */
export default function useRequete(charger, dependances = [], { perimeApres = 5 * 60 * 1000, actif = true } = {}) {
  const [etat, setEtat] = useState({ statut: 'chargement', donnees: undefined, erreur: null, actualisation: false, majLe: null });
  const numero = useRef(0);
  const chargerRef = useRef(charger);
  chargerRef.current = charger;

  const lancer = useCallback(async () => {
    const ceNumero = ++numero.current;
    setEtat((e) => (e.donnees !== undefined
      ? { ...e, actualisation: true }
      : { ...e, statut: 'chargement', erreur: null }));
    try {
      const donnees = await chargerRef.current();
      if (ceNumero !== numero.current) return;           // une réponse plus récente est attendue
      setEtat({ statut: 'pret', donnees, erreur: null, actualisation: false, majLe: Date.now() });
    } catch (erreur) {
      if (ceNumero !== numero.current) return;
      // Des données sont déjà là : on les garde, et l'échec de
      // l'actualisation se signale sans tout remplacer par une erreur.
      setEtat((e) => (e.donnees !== undefined
        ? { ...e, actualisation: false, erreurActualisation: erreur }
        : { statut: 'erreur', donnees: undefined, erreur, actualisation: false, majLe: null }));
    }
  }, []);

  useEffect(() => {
    if (actif) lancer();
    return () => { numero.current += 1; };              // ignorer les réponses d'après démontage
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actif, ...dependances]);

  useEffect(() => {
    const auRetour = () => {
      if (document.visibilityState === 'visible' && etat.majLe && Date.now() - etat.majLe > perimeApres) lancer();
    };
    document.addEventListener('visibilitychange', auRetour);
    return () => document.removeEventListener('visibilitychange', auRetour);
  }, [etat.majLe, perimeApres, lancer]);

  const setDonnees = useCallback((maj) => {
    setEtat((e) => ({ ...e, donnees: typeof maj === 'function' ? maj(e.donnees) : maj }));
  }, []);

  return {
    ...etat,
    perimee: !!etat.majLe && Date.now() - etat.majLe > perimeApres,
    recharger: lancer,
    setDonnees,
  };
}

/** Lire une `Response` fetch en données, ou lever une erreur qui porte son statut. */
export async function donneesOuErreur(reponse, messageParDefaut = "L'opération n'a pas abouti.") {
  const texte = await reponse.text();
  let corps = {};
  try { corps = texte ? JSON.parse(texte) : {}; } catch { corps = {}; }
  if (!reponse.ok) {
    const erreur = new Error(corps.error || corps.detail || messageParDefaut);
    erreur.statut = reponse.status;
    erreur.details = corps;
    throw erreur;
  }
  return corps;
}
